import React, { useState, useEffect } from 'react';
import { validateCredentials, saveSession } from '../utils/auth';

function AuthPage({ onLogin }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [visible, setVisible] = useState(false);
    const [attempts, setAttempts] = useState(0);
    const [lockoutUntil, setLockoutUntil] = useState(null);

    useEffect(() => {
        setTimeout(() => setVisible(true), 30);
    }, []);

    useEffect(() => {
        if (lockoutUntil && Date.now() < lockoutUntil) {
            const timer = setInterval(() => {
                if (Date.now() >= lockoutUntil) {
                    setLockoutUntil(null);
                    setAttempts(0);
                    setError('');
                }
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [lockoutUntil]);

    const handleLogin = (e) => {
        e.preventDefault();
        setError('');

        if (lockoutUntil && Date.now() < lockoutUntil) {
            const remainingSeconds = Math.ceil((lockoutUntil - Date.now()) / 1000);
            setError(`Trop de tentatives. Réessayez dans ${remainingSeconds}s`);
            return;
        }

        if (!username.trim() || !password.trim()) {
            setError('Veuillez remplir tous les champs.');
            return;
        }

        if (validateCredentials(username, password)) {
            setAttempts(0);
            saveSession(username);
            onLogin(username);
        } else {
            const newAttempts = attempts + 1;
            setAttempts(newAttempts);

            if (newAttempts >= 3) {
                setLockoutUntil(Date.now() + 30000);
                setError('Trop de tentatives échouées. Compte verrouillé pour 30 secondes.');
            } else {
                setError(`Identifiants incorrects. (${newAttempts}/3 tentatives)`);
            }
            setPassword('');
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '12px 14px',
        fontSize: '14px',
        border: '1.5px solid #d1d5db',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        fontFamily: 'inherit',
        backgroundColor: '#fafafa',
        transition: 'border-color 0.2s',
    };

    const labelStyle = {
        display: 'block',
        fontSize: '12px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '5px',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
    };

    const isLocked = lockoutUntil && Date.now() < lockoutUntil;

    return (
        <div
            style={{
                minHeight: '100vh',
                background: 'linear-gradient(160deg, #0a1628 0%, #0d2818 40%, #0a1628 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'Segoe UI', system-ui, sans-serif",
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Grille fond */}
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
                    backgroundSize: '40px 40px',
                }}
            />
            {/* Halos */}
            <div
                style={{
                    position: 'absolute',
                    width: '500px',
                    height: '500px',
                    background: 'radial-gradient(circle, rgba(34,197,94,0.12) 0%, transparent 70%)',
                    top: '-100px',
                    left: '-100px',
                    pointerEvents: 'none',
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    width: '400px',
                    height: '400px',
                    background: 'radial-gradient(circle, rgba(59,130,246,0.10) 0%, transparent 70%)',
                    bottom: '-100px',
                    right: '-100px',
                    pointerEvents: 'none',
                }}
            />

            <div
                style={{
                    width: '420px',
                    backgroundColor: 'white',
                    borderRadius: '20px',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                    overflow: 'hidden',
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(24px)',
                    transition: 'opacity 0.5s ease, transform 0.5s ease',
                }}
            >
                {/* Header */}
                <div
                    style={{
                        background: 'linear-gradient(135deg, #166534 0%, #15803d 50%, #1d4ed8 100%)',
                        padding: '32px 32px 28px',
                        textAlign: 'center',
                    }}
                >
                    <div
                        style={{
                            fontSize: '44px',
                            marginBottom: '8px',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                        }}
                    >
                        🚴
                    </div>
                    <h1
                        style={{
                            margin: 0,
                            color: 'white',
                            fontSize: '26px',
                            fontWeight: '800',
                            letterSpacing: '-0.5px',
                            textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        }}
                    >
                        Gravel Tracker
                    </h1>
                    <p
                        style={{
                            margin: '6px 0 0',
                            color: 'rgba(255,255,255,0.75)',
                            fontSize: '13px',
                        }}
                    >
                        Planifiez et analysez vos parcours
                    </p>
                </div>

                {/* Form */}
                <div style={{ padding: '28px 32px 32px' }}>
                    <h2
                        style={{
                            margin: '0 0 20px 0',
                            fontSize: '18px',
                            color: '#374151',
                            textAlign: 'center',
                        }}
                    >
                        Connexion
                    </h2>

                    {error && (
                        <div
                            style={{
                                padding: '12px 14px',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fca5a5',
                                borderRadius: '8px',
                                color: '#dc2626',
                                fontSize: '13px',
                                marginBottom: '18px',
                            }}
                        >
                            ⚠ {error}
                        </div>
                    )}

                    <form onSubmit={handleLogin}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Nom d'utilisateur</label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Entrez votre nom d'utilisateur"
                                autoComplete="username"
                                disabled={isLocked}
                                style={{
                                    ...inputStyle,
                                    opacity: isLocked ? 0.5 : 1,
                                    cursor: isLocked ? 'not-allowed' : 'text',
                                }}
                                onFocus={(e) => !isLocked && (e.target.style.borderColor = '#15803d')}
                                onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
                            />
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>Mot de passe</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                autoComplete="current-password"
                                disabled={isLocked}
                                style={{
                                    ...inputStyle,
                                    opacity: isLocked ? 0.5 : 1,
                                    cursor: isLocked ? 'not-allowed' : 'text',
                                }}
                                onFocus={(e) => !isLocked && (e.target.style.borderColor = '#15803d')}
                                onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={isLocked}
                            style={{
                                width: '100%',
                                padding: '13px',
                                background: isLocked ? '#9ca3af' : 'linear-gradient(135deg, #166534, #15803d)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                fontSize: '15px',
                                fontWeight: '700',
                                cursor: isLocked ? 'not-allowed' : 'pointer',
                                boxShadow: '0 4px 14px rgba(21,128,61,0.35)',
                                fontFamily: 'inherit',
                                opacity: isLocked ? 0.6 : 1,
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) =>
                                !isLocked &&
                                ((e.target.style.transform = 'translateY(-1px)'),
                                    (e.target.style.boxShadow = '0 6px 20px rgba(21,128,61,0.4)'))
                            }
                            onMouseLeave={(e) =>
                                !isLocked &&
                                ((e.target.style.transform = 'translateY(0)'),
                                    (e.target.style.boxShadow = '0 4px 14px rgba(21,128,61,0.35)'))
                            }
                        >
                            {isLocked ? '🔒 Compte verrouillé' : '→ Se connecter'}
                        </button>
                    </form>

                    <p
                        style={{
                            textAlign: 'center',
                            marginTop: '18px',
                            fontSize: '11px',
                            color: '#9ca3af',
                        }}
                    >
                        Accès sécurisé · Gravel Tracker v2.0
                    </p>
                </div>
            </div>
        </div>
    );
}

export default AuthPage;